import {Component, input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Period} from "../../../../core/interfaces/period.interface";
import {count, map, Observable, startWith, Subscription} from "rxjs";
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {AsyncPipe, NgForOf} from "@angular/common";
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteTrigger,
  MatOption
} from "@angular/material/autocomplete";
import {MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatChipsModule} from "@angular/material/chips";
import {MatIconModule} from "@angular/material/icon";
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute} from "@angular/router";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatListItemTitle, MatNavList} from "@angular/material/list";
import {MatIconButton} from "@angular/material/button";
import {LanguageService} from "../../../../shared/services/language.service";

@Component({
  selector: 'app-period-select',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    NgForOf,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    CardsBasicComponent,
    TranslateModule,
    MatCheckboxModule,
    MatNavList,
    MatIconButton,
    MatListItemTitle
  ],
  templateUrl: './period-select.component.html',
  styleUrl: './period-select.component.css'
})
export class PeriodSelectComponent implements OnInit, OnDestroy {
  productData!: any;
  productId!: any;
  periodControl = new FormControl();
  periodList: Period[] = [];
  pageList: any[] = [];
  filteredPeriods!: Observable<Period[]>;
  isLoading: boolean = false;

  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    private _languageService: LanguageService,
    private _translate: TranslateService,
    public _route: ActivatedRoute,) {
  }

  /**
   * Initial method called when the component is initialized.
   * It is responsible for calling the required initialization method(s) for the component.
   *
   * @return {void}
   */
  ngOnInit() {
    this.setupLanguageSubscription()
    this.getRouteParams()
  }

  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }

  private getRouteParams(): void {
    this._route.params.subscribe(params => {
      this.productId = this._route.snapshot.parent?.params['productId'];
      if (this.productId) {
        this.loadProductData()
        this.loadPageList()
      }
    });
  }

  private loadProductData() {
    this.subscriptions.add(
      this._apiService.fetchProductDetail(this.productId)
        .subscribe({
          next: (res: any) => {
            this.productData = res
            this.initPeriodData();
          },
          error: (error) => {
            console.error('There was an error fetching the periods', error);
          }
        })
    )
  }

  private loadPageList() {
    this.subscriptions.add(
      this._apiService.fetchPageList(this.productId)
        .subscribe({
          next: (res: any) => {
            this.pageList = res
          },
          error: (error) => {
            console.error('There was an error fetching the periods', error);
          }
        })
    )
  }

  /**
   * Initializes the period data by fetching the period list from the API service
   * and setting the filtered periods.
   *
   * @private
   * @returns {void}
   */
  private initPeriodData(): void {
    this.subscriptions.add(
      this._apiService.fetchPeriodList()
        .subscribe({
          next: (res: any) => {
            this.periodList = res;
            this.setFilteredPeriods();
          },
          error: (error) => {
            console.error('There was an error fetching the periods', error);
          }
        })
    );
  }

  /**
   * Set the filtered periods based on the value changes of the period control.
   *
   * @private
   * @returns {void}
   */
  private setFilteredPeriods(): void {
    this.filteredPeriods = this.periodControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value.translations['en'].title),
      map(title => title ? this._filter(title) : this.periodList.slice())
    );
  }

  /**
   * Filters the period list based on the given title.
   *
   * @param {string} title - The title to filter by.
   * @return {Period[]} - The filtered period list.
   */
  private _filter(title: string): Period[] {
    const filterValue = title.toLowerCase();
    return this.periodList.filter(option => option.translations['en'].title.toLowerCase().includes(filterValue));
  }

  /**
   * Assigns a period to the product data.
   *
   * @param {Period} period - The period to be assigned.
   * @return {void}
   */
  assignPeriod(period: Period): void {
    this.periodControl.setValue('');
    if (!this.productData.periods.find((p: any) => p.id === period.id)) {
      this.productData.periods.push(period);
      const periodIds = this.productData.periods.map((p: any) => p.id);
      this.updateProductPeriod(periodIds);
    }
  }

  /**
   * Unassigns a period from the product data.
   *
   * @param {Period} period - The period to be unassigned.
   * @return {void}
   */
  unassignPeriod(period: Period): void {
    const periodIndex = this.productData.periods.findIndex((p: any) => p.id === period.id);

    if (periodIndex !== -1) {
      this.productData.periods.splice(periodIndex, 1);
      const periodIds = this.productData.periods.map((p: any) => p.id);
      this.updateProductPeriod(periodIds);
    }

    // Remove the period from all pages where it is assigned
    this.pageList.forEach((page) => {
      if (this.isPeriodAssignedToPage(page, period)) {
        this.removePeriodFromPage(page, period);
      }
    });
  }

  /**
   * Updates the periods of a product.
   *
   * @param {any} periodIds - The IDs of the periods to be updated for the product.
   * @return {void}
   */
  private updateProductPeriod(periodIds: any): void {
    this.subscriptions.add(
      this._apiService.updateProductPeriods(this.productData.id, periodIds)
        .subscribe({
          next: (res: any) => {
            this._snackbarService.show(res.message);
          },
          error: (error) => {
            console.error('There was an error updating the product', error);
          }
        }));
  }

  isPeriodAssignedToPage(page: any, period: any): boolean {
    return page.periods.some((p: any) => p.id === period.id);
  }

  togglePeriodAssignment(page: any, period: any, isChecked: boolean): void {
    if (isChecked) {
      this.addPeriodToPage(page, period)
    } else {
      this.removePeriodFromPage(page, period)
    }
  }

  private addPeriodToPage(page: any, period: any): void {
    this.subscriptions.add(
      this._apiService.addPeriodToPage(page.id, period.id)
        .subscribe({
          next: (response: any) => {
            page.periods.push(period);
            this._snackbarService.show(response.message);
          },
          error: (error) => {
            console.error('Failed to add period to page', error);
          }
        }));
  }

  private removePeriodFromPage(page: any, period: any): void {
    this.subscriptions.add(
      this._apiService.removePeriodFromPage(page.id, period.id)
        .subscribe({
          next: (response: any) => {
            this._snackbarService.show(response.message);
            const index = page.periods.findIndex((p: any) => p.id === period.id);
            if (index !== -1) {
              page.periods.splice(index, 1);
            }
          },
          error: (error) => {
            console.error('Failed to remove period from page', error);
          }
        }));
  }


  /**
   * Cleans up any resources used by the component when it is destroyed.
   */
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

}
